import React from 'react';
import { Users, Zap, Gift, Heart, MapPin } from 'lucide-react';

const AdvantagesSection = () => {
  const advantages = [
    {
      icon: Users,
      title: "Communauté active",
      description: "Partagez et découvrez des spots grâce à une carte interactive collaborative enrichie par les utilisateurs.",
      color: "from-blue-400 to-blue-600"
    },
    {
      icon: Zap,
      title: "Simplicité d'utilisation",
      description: "Localisez rapidement les meilleurs spots fixes, sans effort de recherche, avec des détails pratiques complets.",
      color: "from-green-400 to-green-600"
    },
    {
      icon: Gift,
      title: "Accès libre et gratuit",
      description: "Aucun abonnement ni limitation, Sun-Spot est ouvert à tous sans conditions.",
      color: "from-purple-400 to-purple-600"
    },
    {
      icon: Heart,
      title: "Pertinence émotionnelle",
      description: "Les tags de mood vous permettent de trouver un endroit qui correspond précisément à votre humeur du moment (romantique, paisible, contemplatif, etc.).",
      color: "from-pink-400 to-pink-600"
    },
    {
      icon: MapPin,
      title: "Soutien local",
      description: "Potentiel futur de publicité pertinente uniquement basée sur des commerces locaux, restaurants et événements à proximité immédiate des spots.",
      color: "from-amber-400 to-orange-600"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-amber-900 mb-6">
            Pourquoi Sun-Spot ?
          </h2>
          <p className="text-xl text-amber-600 max-w-3xl mx-auto">
            5 raisons de rejoindre notre communauté
          </p>
        </div>

        {/* Advantages Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {advantages.map((advantage, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="flex items-start space-x-6">
                <div className={`p-4 bg-gradient-to-br ${advantage.color} rounded-xl shadow-md`}>
                  <advantage.icon className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-amber-900 mb-4">
                    {advantage.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    {advantage.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Feature - Mood Tags */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-amber-900 mb-4">
              Tags d'humeur personnalisés
            </h3>
            <p className="text-gray-700 text-lg max-w-3xl mx-auto">
              Trouvez exactement ce que vous cherchez selon votre état d'esprit du moment
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { tag: 'Romantique', emoji: '💕', color: 'bg-pink-100 text-pink-700' },
              { tag: 'Paisible', emoji: '🧘', color: 'bg-green-100 text-green-700' },
              { tag: 'Contemplatif', emoji: '🌅', color: 'bg-amber-100 text-amber-700' },
              { tag: 'Inspirant', emoji: '✨', color: 'bg-purple-100 text-purple-700' },
              { tag: 'Zen', emoji: '🕯️', color: 'bg-blue-100 text-blue-700' },
              { tag: 'Aventure', emoji: '⛰️', color: 'bg-orange-100 text-orange-700' },
              { tag: 'Méditation', emoji: '🧘‍♀️', color: 'bg-indigo-100 text-indigo-700' },
              { tag: 'Photo', emoji: '📸', color: 'bg-teal-100 text-teal-700' }
            ].map((mood, index) => (
              <div 
                key={index}
                className={`${mood.color} rounded-lg p-4 text-center hover:scale-105 transition-transform duration-200 cursor-pointer`}
              >
                <div className="text-2xl mb-2">{mood.emoji}</div>
                <div className="font-semibold">{mood.tag}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center p-8 bg-white rounded-xl shadow-md">
            <div className="text-4xl font-bold text-amber-600 mb-2">100%</div>
            <div className="text-gray-700 font-medium">Gratuit et sans pub</div>
          </div>
          <div className="text-center p-8 bg-white rounded-xl shadow-md">
            <div className="text-4xl font-bold text-amber-600 mb-2">∞</div>
            <div className="text-gray-700 font-medium">Spots à découvrir</div>
          </div>
          <div className="text-center p-8 bg-white rounded-xl shadow-md">
            <div className="text-4xl font-bold text-amber-600 mb-2">🌍</div>
            <div className="text-gray-700 font-medium">Communauté mondiale</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdvantagesSection;